import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { X, Star, Lightbulb, Sparkles, Target, Users, ListChecks, Clock, FileText, CheckCircle2, Minus, Plus, ChevronLeft, ChevronRight, Building2, Award, ExternalLink } from 'lucide-react';
import { getB2bTraining, dienRfqIn } from '../../services/b2bAcademyApi';
import type { B2bTraining, B2bTrainingDetail, NieuweRfq, RfqLocatie, RfqPlanning, Rfq } from '../../services/b2bAcademyApi';
import { INKOOP, LEERVORM, LOCATIE_OPTIES, PLANNING_OPTIES, WKR_ORANJE, prijs } from './b2bStijl';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

/** Contentmatrix-blok (spec §2) met icoon; opleider-invoer is platte tekst. */
const Matrixblok: React.FC<{ icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; titel: string; tekst: string | null | undefined; accent?: string }> = ({ icon: Icon, titel, tekst, accent }) =>
  tekst?.trim() ? (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
        <Icon className="h-4 w-4" style={accent ? { color: accent } : undefined} aria-hidden="true" />
        {titel}
      </h3>
      <p className="mt-1.5 whitespace-pre-line text-[15px] leading-relaxed text-gray-700">{tekst.trim()}</p>
    </section>
  ) : null;

const ReviewBalk: React.FC<{ label: string; waarde: number }> = ({ label, waarde }) => (
  <div>
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="inline-flex items-center gap-1 font-semibold text-gray-900">
        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
        {waarde.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      </span>
    </div>
    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={1} aria-valuemax={5} aria-valuenow={waarde} aria-label={label}>
      <div className="h-full rounded-full bg-amber-400" style={{ width: `${(waarde / 5) * 100}%` }} />
    </div>
  </div>
);

const STAPPEN = 4;

/** De 4-staps Digitale B2B RFQ Wizard (spec §4): omvang, locatie, planning, casuïstiek. */
const RfqWizard: React.FC<{ training: B2bTraining; onKlaar: (rfq: Rfq) => void; onAnnuleer: () => void }> = ({ training, onKlaar, onAnnuleer }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [stap, setStap] = useState(1);
  const [afdeling, setAfdeling] = useState('');
  const [deelnemers, setDeelnemers] = useState(12);
  const [locatie, setLocatie] = useState<RfqLocatie | null>(null);
  const [planning, setPlanning] = useState<RfqPlanning | null>(null);
  const [notities, setNotities] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const invoer: NieuweRfq = { afdeling: afdeling.trim() || null, deelnemers, locatie_voorkeur: locatie!, planning: planning!, notities: notities.trim() || null };
      return dienRfqIn(training.id, invoer);
    },
    onSuccess: (rfq) => {
      queryClient.invalidateQueries({ queryKey: ['b2b-academy'] });
      onKlaar(rfq);
    },
  });

  const kanVerder = stap === 1 ? deelnemers >= 1 : stap === 2 ? locatie !== null : stap === 3 ? planning !== null : true;
  const optieKnop = 'flex w-full items-center gap-3 rounded-xl border p-3 text-left text-sm transition';

  return (
    <div className="rounded-2xl border border-sky-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-gray-900">{t('b2bAcademy.wizard.titel', { defaultValue: 'Digitale offerte-aanvraag' })}</h3>
        <span className="text-xs text-gray-500">{t('b2bAcademy.wizard.stap', { defaultValue: 'Stap {{n}} van {{totaal}}', n: stap, totaal: STAPPEN })}</span>
      </div>
      <div className="mt-2 flex gap-1" aria-hidden="true">
        {Array.from({ length: STAPPEN }, (_, i) => (
          <span key={i} className={`h-1 flex-1 rounded-full ${i < stap ? 'bg-sky-500' : 'bg-gray-200'}`} />
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {stap === 1 && (
          <>
            <p className="text-sm font-semibold text-gray-900">{t('b2bAcademy.wizard.omvang', { defaultValue: 'Omvang & doelgroep (capaciteit)' })}</p>
            <input
              type="text"
              value={afdeling}
              onChange={(e) => setAfdeling(e.target.value)}
              maxLength={120}
              placeholder={t('b2bAcademy.wizard.afdelingPh', { defaultValue: 'Afdeling / team…' })}
              aria-label={t('b2bAcademy.wizard.afdeling', { defaultValue: 'Afdeling of team' })}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-sky-500 focus:outline-none"
            />
            <div className="flex items-center justify-center gap-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <button type="button" onClick={() => setDeelnemers((n) => Math.max(1, n - 1))} aria-label={t('b2bAcademy.wizard.minder', { defaultValue: 'Minder deelnemers' })} className="rounded-full border border-gray-300 bg-white p-2 text-gray-900 hover:bg-gray-100">
                <Minus className="h-4 w-4" aria-hidden="true" />
              </button>
              <div className="text-center">
                <p className="text-3xl font-extrabold text-gray-900">{deelnemers}</p>
                <p className="text-xs text-gray-500">{t('b2bAcademy.wizard.deelnemers', { defaultValue: 'Deelnemers' })}</p>
              </div>
              <button type="button" onClick={() => setDeelnemers((n) => Math.min(5000, n + 1))} aria-label={t('b2bAcademy.wizard.meer', { defaultValue: 'Meer deelnemers' })} className="rounded-full border border-gray-300 bg-white p-2 text-gray-900 hover:bg-gray-100">
                <Plus className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </>
        )}
        {stap === 2 && (
          <>
            <p className="text-sm font-semibold text-gray-900">{t('b2bAcademy.wizard.locatie', { defaultValue: 'Locatie-voorkeur' })}</p>
            {LOCATIE_OPTIES.map((o) => (
              <button key={o.key} type="button" onClick={() => setLocatie(o.key)} aria-pressed={locatie === o.key} className={`${optieKnop} ${locatie === o.key ? 'border-sky-500 bg-sky-50 text-gray-900' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'}`}>
                <span className="text-xl" aria-hidden="true">{o.emoji}</span>
                <span>
                  <span className="block font-semibold">{t(`b2bAcademy.locatie.${o.key}`, { defaultValue: o.label })}</span>
                  <span className="block text-xs text-gray-500">{t(`b2bAcademy.locatieUitleg.${o.key}`, { defaultValue: o.uitleg })}</span>
                </span>
              </button>
            ))}
          </>
        )}
        {stap === 3 && (
          <>
            <p className="text-sm font-semibold text-gray-900">{t('b2bAcademy.wizard.planning', { defaultValue: 'Gewenste planning' })}</p>
            {PLANNING_OPTIES.map((o) => (
              <button key={o.key} type="button" onClick={() => setPlanning(o.key)} aria-pressed={planning === o.key} className={`${optieKnop} ${planning === o.key ? 'border-sky-500 bg-sky-50 text-gray-900' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'}`}>
                <span className="text-xl" aria-hidden="true">{o.emoji}</span>
                <span className="font-semibold">{t(`b2bAcademy.planning.${o.key}`, { defaultValue: o.label })}</span>
              </button>
            ))}
          </>
        )}
        {stap === 4 && (
          <>
            <p className="text-sm font-semibold text-gray-900">{t('b2bAcademy.wizard.casuistiek', { defaultValue: 'Casuïstiek & maatwerk-notities' })}</p>
            <textarea
              value={notities}
              onChange={(e) => setNotities(e.target.value)}
              rows={4}
              maxLength={4000}
              placeholder={t('b2bAcademy.wizard.notitiesPh', { defaultValue: 'bijv. Graag de nadruk leggen op de communicatie tussen de dag- en nachtploeg' })}
              aria-label={t('b2bAcademy.wizard.casuistiek', { defaultValue: 'Casuïstiek & maatwerk-notities' })}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-sky-500 focus:outline-none"
            />
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs">
              <dt className="text-gray-500">{t('b2bAcademy.wizard.deelnemers', { defaultValue: 'Deelnemers' })}</dt>
              <dd className="text-gray-900">{deelnemers}{afdeling.trim() ? ` · ${afdeling.trim()}` : ''}</dd>
              <dt className="text-gray-500">{t('b2bAcademy.wizard.locatie', { defaultValue: 'Locatie-voorkeur' })}</dt>
              <dd className="text-gray-900">{LOCATIE_OPTIES.find((o) => o.key === locatie)?.label}</dd>
              <dt className="text-gray-500">{t('b2bAcademy.wizard.planning', { defaultValue: 'Gewenste planning' })}</dt>
              <dd className="text-gray-900">{PLANNING_OPTIES.find((o) => o.key === planning)?.label}</dd>
            </dl>
          </>
        )}
      </div>

      {mutation.isError && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {foutMelding(mutation.error, t('b2bAcademy.wizard.fout', { defaultValue: 'De aanvraag kon niet worden ingediend.' }))}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between gap-2">
        <button type="button" onClick={() => (stap === 1 ? onAnnuleer() : setStap((s) => s - 1))} className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900">
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {stap === 1 ? t('b2bAcademy.wizard.annuleer', { defaultValue: 'Annuleren' }) : t('b2bAcademy.wizard.terug', { defaultValue: 'Terug' })}
        </button>
        {stap < STAPPEN ? (
          <button type="button" onClick={() => setStap((s) => s + 1)} disabled={!kanVerder} className="inline-flex items-center gap-1 rounded-xl bg-[#0B1F3A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1A3459] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500">
            {t('b2bAcademy.wizard.volgende', { defaultValue: 'Volgende' })}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : (
          <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:brightness-110 disabled:opacity-60">
            <FileText className="h-4 w-4" aria-hidden="true" />
            {mutation.isPending ? '…' : t('b2bAcademy.wizard.indienen', { defaultValue: 'Offerte-aanvraag indienen' })}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Informatie-scherm (Full Detail Pop-up) voor de HR-manager op een licht, gebroken wit
 * achterscherm: links (70%) de contentmatrix (spec §2), rechts (30%) de vanaf-prijsopbouw,
 * de fiscale status en de RFQ-wizard. `startWizard` (⚡ Direct Anoniem Boeken op de tegel)
 * opent de wizard meteen.
 */
export const B2bDetailModal: React.FC<{ training: B2bTraining; openRfq?: Rfq | null; startWizard?: boolean; onClose: () => void }> = ({ training: basis, openRfq, startWizard = false, onClose }) => {
  const { t } = useTranslation('pages');
  const sluitKnop = useRef<HTMLButtonElement>(null);
  const [wizard, setWizard] = useState(startWizard);
  const [ingediend, setIngediend] = useState<Rfq | null>(null);

  const detailQuery = useQuery({ queryKey: ['b2b-academy-training', basis.id], queryFn: () => getB2bTraining(basis.id) });
  const detail: B2bTrainingDetail | undefined = detailQuery.data;
  const tr: B2bTraining = detail ?? basis;
  const accent = tr.rij_kleur;
  const inkoop = INKOOP[tr.inkoop_type];
  const InkoopIcon = inkoop.icon;
  const vorm = LEERVORM[tr.leervorm];
  const VormIcon = vorm.icon;
  const lopend = ingediend ?? openRfq ?? null;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    sluitKnop.current?.focus();
    const vorig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const toets = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
    window.addEventListener('keydown', toets);
    return () => {
      document.body.style.overflow = vorig;
      window.removeEventListener('keydown', toets);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="b2b-detail-titel"
        className="relative mx-auto my-0 min-h-full w-full max-w-6xl overflow-hidden bg-gray-50 text-gray-700 shadow-2xl ring-1 ring-gray-200 sm:my-8 sm:min-h-0 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-video max-h-[26rem] w-full overflow-hidden sm:aspect-[21/8]" style={{ background: `radial-gradient(120% 90% at 30% 20%, ${accent}55 0%, #0b1020 60%, #05070c 100%)` }}>
          {tr.omslagfoto_url ? (
            <img src={tr.omslagfoto_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-8xl" aria-hidden="true">{tr.rij_emoji}</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/90 via-[#0A192F]/30 to-transparent" aria-hidden="true" />
          <button ref={sluitKnop} type="button" onClick={onClose} aria-label={t('b2bAcademy.sluiten', { defaultValue: 'Sluiten' })} className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-white backdrop-blur hover:bg-black">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="absolute inset-x-0 bottom-0 px-5 pb-4 sm:px-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-gray-900 shadow-lg">
                <InkoopIcon className="h-4 w-4 text-[#0B1F3A]" aria-hidden="true" />
                {t(`b2bAcademy.inkoop.${tr.inkoop_type}`, { defaultValue: inkoop.label })}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white" style={{ backgroundColor: `${accent}cc` }}>
                {tr.rij_emoji} {tr.rij_label}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-white">
                <VormIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(`b2bAcademy.leervorm.${tr.leervorm}`, { defaultValue: vorm.label })}
              </span>
              {tr.roi_rating && (
                <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-xs font-bold text-white">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                  {tr.roi_rating.gemiddelde.toLocaleString('nl-NL', { minimumFractionDigits: 1 })} <span className="font-normal text-slate-300">{t('b2bAcademy.tegel.roi', { defaultValue: 'ROI Rating' })}</span>
                </span>
              )}
            </div>
            <h2 id="b2b-detail-titel" className="mt-2 text-2xl font-extrabold leading-tight text-white sm:text-4xl">{tr.titel}</h2>
          </div>
        </div>

        <div className="grid gap-8 px-5 pb-8 pt-4 sm:px-8 lg:grid-cols-[7fr_3fr]">
          <div className="min-w-0 space-y-4">
            {tr.short_pitch && (
              <p className="flex items-start gap-2 text-lg font-semibold leading-snug text-gray-900">
                <Lightbulb className="mt-1 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
                {tr.short_pitch}
              </p>
            )}
            {detailQuery.isLoading && <p className="text-sm text-gray-500">…</p>}
            {detailQuery.isError && (
              <p className="text-sm text-red-600" role="alert">{foutMelding(detailQuery.error, t('b2bAcademy.detail.laadfout', { defaultValue: 'De details konden niet worden geladen.' }))}</p>
            )}
            {detail && (
              <>
                <Matrixblok icon={Users} titel={t('b2bAcademy.detail.doelgroep', { defaultValue: 'Voor wie binnen uw organisatie' })} tekst={detail.content.target_audience} accent={accent} />
                <Matrixblok icon={Target} titel={t('b2bAcademy.detail.doel', { defaultValue: 'Het organisatie-doel (ROI)' })} tekst={detail.content.organizational_goal} accent={accent} />
                <Matrixblok icon={ListChecks} titel={t('b2bAcademy.detail.programma', { defaultValue: 'Wat wordt er aangeboden' })} tekst={detail.content.program_description} accent={accent} />
                <Matrixblok icon={Clock} titel={t('b2bAcademy.detail.duur', { defaultValue: 'Duur & vorm' })} tekst={detail.content.duration_logistics} accent={accent} />
                <Matrixblok icon={Award} titel={t('b2bAcademy.detail.leerdoelen', { defaultValue: 'Leerdoelen' })} tekst={detail.content.leerdoelen} accent={accent} />
                <section>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t('b2bAcademy.detail.reviews', { defaultValue: 'Organisatie-impact (beoordelingen van werkgevers)' })}</h3>
                  {detail.reviews.balken ? (
                    <div className="mt-2 grid gap-3 sm:grid-cols-3">
                      <ReviewBalk label={t('b2bAcademy.detail.relevantie', { defaultValue: 'Relevantie voor de organisatie' })} waarde={detail.reviews.balken.relevantie} />
                      <ReviewBalk label={t('b2bAcademy.detail.toepasbaarheid', { defaultValue: 'Direct toepasbaar' })} waarde={detail.reviews.balken.toepasbaarheid} />
                      <ReviewBalk label={t('b2bAcademy.detail.prijsKwaliteit', { defaultValue: 'Prijs / kwaliteit' })} waarde={detail.reviews.balken.prijs_kwaliteit} />
                    </div>
                  ) : (
                    <p className="mt-1.5 text-sm text-gray-500">{t('b2bAcademy.detail.nogGeenReviews', { defaultValue: 'ROI-rating zichtbaar vanaf 5 beoordelingen van werkgevers ({{n}} tot nu toe).', n: detail.reviews.aantal })}</p>
                  )}
                </section>
                <dl className="grid gap-x-6 gap-y-3 rounded-xl border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500">{t('b2bAcademy.detail.aanbieder', { defaultValue: 'Erkende aanbieder' })}</dt>
                    <dd className="inline-flex flex-wrap items-center gap-2 font-semibold text-emerald-700">
                      <Building2 className="h-4 w-4" aria-hidden="true" />
                      {tr.organisatie_naam}
                      {detail.provider_website && (
                        <a href={detail.provider_website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-normal text-sky-700 hover:text-sky-900">
                          {t('b2bAcademy.detail.website', { defaultValue: 'Website' })} <ExternalLink className="h-3 w-3" aria-hidden="true" />
                        </a>
                      )}
                    </dd>
                  </div>
                  {detail.content.docenten && (
                    <div>
                      <dt className="text-gray-500">{t('b2bAcademy.detail.docenten', { defaultValue: 'Trainers' })}</dt>
                      <dd className="text-gray-900">{detail.content.docenten}</dd>
                    </div>
                  )}
                  {detail.content.accreditatie && (
                    <div>
                      <dt className="text-gray-500">{t('b2bAcademy.detail.accreditatie', { defaultValue: 'Accreditatie' })}</dt>
                      <dd className="text-gray-900">{detail.content.accreditatie}</dd>
                    </div>
                  )}
                  {(tr.locatie_detail || tr.locaties) && (
                    <div>
                      <dt className="text-gray-500">{t('b2bAcademy.detail.locatie', { defaultValue: 'Locatie' })}</dt>
                      <dd className="text-gray-900">{tr.locatie_detail || tr.locaties}</dd>
                    </div>
                  )}
                </dl>
              </>
            )}
          </div>

          <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900">{t('b2bAcademy.detail.prijsindicatie', { defaultValue: 'Prijsindicatie per groep' })}</h3>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">{t('b2bAcademy.detail.marktprijs', { defaultValue: 'Normale projectprijs (vanaf)' })}</dt>
                  <dd className="text-gray-600"><s>{prijs(tr.vanaf_prijs)}</s></dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="inline-flex items-center gap-1 text-emerald-700">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('b2bAcademy.detail.voordeel', { defaultValue: 'BeLagom B2B Voordeel (5%)' })}
                  </dt>
                  <dd className="whitespace-nowrap text-emerald-700">− {prijs(tr.vanaf_korting)}</dd>
                </div>
                <div className="flex justify-between gap-3 border-t border-gray-200 pt-2 text-base font-bold text-gray-900">
                  <dt>{t('b2bAcademy.detail.postPay', { defaultValue: 'Vanaf, achteraf te factureren' })}</dt>
                  <dd className="whitespace-nowrap">{prijs(tr.vanaf_netto)}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs text-gray-500">{t('b2bAcademy.detail.prijsUitleg', { defaultValue: 'Excl. btw. De definitieve offertewaarde volgt uit uw aanvraag (deelnemers, locatie, maatwerk).' })}</p>
              <p
                className="mt-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium"
                style={tr.wkr_belast ? { borderColor: `${WKR_ORANJE}80`, color: '#B45309', backgroundColor: `${WKR_ORANJE}14` } : { borderColor: '#34d39966', color: '#047857', backgroundColor: '#34d39914' }}
              >
                {tr.wkr_belast ? '⚠️' : '💸'} {tr.fiscaal_label}
              </p>
            </div>

            {lopend ? (
              <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center" role="status">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
                <p className="mt-2 text-lg font-bold text-gray-900">
                  {ingediend ? t('b2bAcademy.detail.ingediend', { defaultValue: 'Uw offerte-aanvraag is ingediend!' }) : t('b2bAcademy.detail.loopt', { defaultValue: 'Er loopt een offerte-aanvraag' })}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  {t('b2bAcademy.detail.ingediendUitleg', { defaultValue: 'De opleider ontvangt direct een signaal en stelt de offerte op. U vindt de status en de financiële breakdown onder "Mijn offerte-aanvragen".' })}
                </p>
              </div>
            ) : wizard ? (
              <RfqWizard training={tr} onKlaar={setIngediend} onAnnuleer={() => setWizard(false)} />
            ) : (
              <button
                type="button"
                onClick={() => setWizard(true)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold text-white shadow-lg transition hover:brightness-110"
                style={{ backgroundColor: '#0284c7', boxShadow: '0 10px 30px -10px rgba(2,132,199,.7)' }}
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                {t('b2bAcademy.detail.startRfq', { defaultValue: '📄 Start Digitale Offerte-Aanvraag' })}
              </button>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default B2bDetailModal;
